import React, { useState, useMemo, useEffect } from 'react';
import dayjs from 'dayjs';
import {
    Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Tooltip,Legend
} from 'recharts';
 
import { Box, Typography,FormControl,ListItemText,InputLabel ,Select,Checkbox,MenuItem} from '@mui/material';
 
// Custom Dot Components
const CustomDot1 = ({ cx, cy, value }) => (
    <g>
        <circle cx={cx} cy={cy} r={3} fill="#1890FF" strokeWidth={2} />
        <text x={cx} y={cy - 10} fontSize={12} textAnchor="middle" fill="#17428B">
            {value}
        </text>
    </g>
);
 
const CustomDot2 = ({ cx, cy, value }) => (
    <g>
        <circle cx={cx} cy={cy} r={3} fill="#17428B" strokeWidth={2} />
        <text x={cx} y={cy - 10} fontSize={12} textAnchor="middle" fill="#17428B">
            {value}
        </text>
    </g>
);
 
// Custom components for ticks (Radius and Angle)
const CustomAngleTick = ({ payload, x, y, textAnchor }) => {
    const subject = payload.value;
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                textAnchor={textAnchor}
                fill="#17428B"
                fontSize={10}
                fontWeight={500}
                dy={0}
                dx={0}
            >
                {subject}
            </text>
        </g>
    );
};
 
const CustomRadiusTick = ({ payload, x, y }) => {
    if (payload.value === 0) return null;
    const textWidth = 24;
    const textHeight = 18;
    return (
        <g>
            <rect
                x={x - textWidth / 2}
                y={y + 3}
                width={textWidth}
                height={textHeight}
                fill="#E7E7E7"
                rx={4}
            />
            <text
                x={x}
                y={y}
                fill="#17428B"
                fontSize={8}
                fontWeight={500}
                textAnchor="middle"
                dy={16}
            >
                {payload.value}
            </text>
        </g>
    );
};








const RadarChartComponent = ({ groupRadarData, years, fetchGroupLeaderDataRadarchart, selectedYearGroupRadar, setSelectedYearGroupRadar, selectedMonthsGroupRadar, setSelectedMonthsGroupRadar, groupLeaderRole, zones }) => {
    const monthOptions = Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: dayjs().month(i).format('MMMM'),
    }));

    const colorPalette = [
        "#FF5733", "#33FF57", "#3357FF", "#FF33A1", "#A133FF", "#33FFF1",
        "#FFC133", "#8DFF33", "#FF338F", "#33FF8D", "#8D33FF", "#FF8D33",
    ];

    const handleYearChange = (event) => {
        const year = event.target.value;
        setSelectedYearGroupRadar(year);
        fetchGroupLeaderDataRadarchart(groupLeaderRole.zone_group_id, year, selectedMonthsGroupRadar);
    };

    const handleMonthChange = (event) => {
        const value = event.target.value; // MUI Select provides the value directly
        setSelectedMonthsGroupRadar(value);
        fetchGroupLeaderDataRadarchart(groupLeaderRole.zone_group_id, selectedYearGroupRadar, value);
    };

    const radarData = useMemo(() => {
        if (selectedMonthsGroupRadar.length === 0) {
            return []; // Return an empty array if no months are selected
        }
        // Create an array to hold the scores for each zone for each selected month
        const data = zones.map(zone => {
            const zoneData = { subject: zone.name }; // Initialize with the zone name
            selectedMonthsGroupRadar.forEach(month => {
                const scores = groupRadarData.filter(item => item.zone_name === zone.name && item.audit_month === month.toString() && item.audit_year === selectedYearGroupRadar.toString());
                // If there are scores for the month, take the first score (or sum if needed)
                zoneData[`month${month}`] = scores.length > 0 ? scores[0].total_score : 0; // Use 0 if no score
            });
            return zoneData;
        });
        return data;
    }, [groupRadarData, zones, selectedMonthsGroupRadar, selectedYearGroupRadar]);

    // Always render the select boxes
    return (
        <Box sx={{ border: '1px solid #ccc', borderRadius: '5px', width: '100%' }}>
            <Box sx={{ borderBottom: '1px solid #ccc', padding: '12px', display: {
          md:'flex', xs: "block"
         }, justifyContent: 'space-between', alignItems: 'center' }}>
             <Typography variant='h4' sx={{ color: '#17428B', fontSize: '16px', fontWeight: '600', marginBottom: {md:'0',xs:'12px'} }}>6S Audit Summary - India</Typography>
              
                   <Box
                           sx={{
                             display: "flex",
                             gap: "8px",
                             alignItems: "center",
                             flexWrap: "wrap",
                             justifyContent: { xs: "flex-end", md: "flex-start" }, // Align right on mobile
                           }}
                         >
                    <Select
                        value={selectedYearGroupRadar}
                        onChange={handleYearChange}
                        sx={{
                            height: "32px",
                            fontSize: "14px",
                            padding: "4px 0px",
                            minWidth: "100px", // Set a fixed width
                            maxWidth: "120px", // Prevents expansion
                            width: "120px",
                            flexShrink: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                    >
                        {years.map(year => (
                            <MenuItem key={year} value={year}>{year}</MenuItem>
                        ))}
                    </Select>
               
               
                    <Select
                        multiple
                        value={selectedMonthsGroupRadar || []}
                        onChange={handleMonthChange}
                        renderValue={(selected) =>
                            selected
                                .map((monthValue) => monthOptions.find((m) => m.value === monthValue)?.label)
                                .join(", ")
                        }
                        sx={{
                            height: "32px",
                            fontSize: "14px",
                            padding: "4px 0px",
                            minWidth: "100px", // Set a fixed width
                            maxWidth: "120px", // Prevents expansion
                            width: "120px",
                            flexShrink: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                    >
                        {monthOptions.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                                <Checkbox checked={selectedMonthsGroupRadar.includes(option.value)} />
                                <ListItemText primary={option.label} />
                            </MenuItem>
                        ))}
                    </Select>
              
            </Box>
            </Box>
            {/* Display radar chart or a message if no data is available */}
            {radarData.length === 0 ? (
                <div>No data available for the selected year and month(s).</div>
            ) : (
                <Box
                        sx={{
                          width: "100%",
                          overflow: "hidden",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: window.innerWidth > 768 ? "600px" : "500px", // Increased height for desktop
                          flexDirection: "column",
                        
                        }}
                      >
                <RadarChart 
                outerRadius={window.innerWidth > 768 ? 180 : 120} // Adjust size based on screen width
                width={window.innerWidth > 768 ? 650 : 350}
                height={window.innerWidth > 768 ? 500 : 400}
                data={radarData}
                 >
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" 
               tick={({ payload, x, y, textAnchor }) => {
                const text = payload.value;
                const maxCharsPerLine = 25; // Wrap after 20 characters
                let words = [];
                
                for (let i = 0; i < text.length; i += maxCharsPerLine) {
                  words.push(text.substring(i, i + maxCharsPerLine));
                }
            
                return (
                  <text x={x} y={y} textAnchor={textAnchor} fontSize={12} fill="#000">
                    {words.map((line, index) => (
                      <tspan key={index} x={x} dy={index * 14}>
                        {line.trim()}
                      </tspan>
                    ))}
                  </text>
                );
              }}
                    />
                    {selectedMonthsGroupRadar.map((month, index) => (
                        <Radar
                            key={month}
                            name={`Month ${month}`}
                            dataKey={`month${month}`}
                            stroke={colorPalette[index % colorPalette.length]}
                            fill={colorPalette[index % colorPalette.length]}
                            fillOpacity={0.3}
                        />
                    ))}
                    <Tooltip />
                    <Legend 
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="left"
                    // wrapperStyle={{
                       
                    //     padding: "10px",
                    //     width: "100%",
                    //     display: "flex",
                    //     justifyContent: "center",
                    //     bottom:'1px'
                    //   }}
                    wrapperStyle={{
                        position: "absolute",
                        bottom: "1px", // Push it to the bottom
                        width: "100%",
                        textAlign: "center",
                        display: "flex",
                        padding: "10px"
                      }}
                     />
                </RadarChart>
                </Box>
            )}
        </Box>
    );
};

export default RadarChartComponent;
//prod code
// const renderCustomTick = ({ x, y, payload, textAnchor }) => {
//     const MAX_LINES = 2; // Ensure a maximum of two lines
//     const text = payload.value;

//     // Split the text into two lines
//     const midpoint = Math.ceil(text.length / MAX_LINES);
//     const lines = [
//         text.slice(0, midpoint), // First half
//         text.slice(midpoint)     // Second half
//     ];

//     const lineHeight = 14; // Adjust the spacing between the lines

//     return (
//         <g transform={`translate(${x},${y})`}>
//             {lines.map((line, index) => (
//                 <text
//                     key={index}
//                     x={0}
//                     y={index * lineHeight}
//                     textAnchor={textAnchor}
//                     fill="#17428B"
//                     fontSize={12}
//                 >
//                     {line}
//                 </text>
//             ))}
//         </g>
//     );
// };


// // Usage in your PolarAngleAxis
// <PolarAngleAxis
//     dataKey="subject"
//     tick={renderCustomTick}
// />



// //test code
// const RadarChartComponent = ({ groupLeaderChartData }) => {
//     const currentYear = dayjs().year();
//     const currentMonth = dayjs().month() + 1;

//     const [selectedYear, setSelectedYear] = useState(currentYear);
//     const [selectedMonths, setSelectedMonths] = useState([currentMonth]);

//     const monthOptions = Array.from({ length: 12 }, (_, i) => ({
//         value: i + 1,
//         label: dayjs().month(i).format('MMMM'),
//     }));

//     const handleMonthChange = (event) => {
//         setSelectedMonths(event.target.value);
//     };

//     // Get unique zone names statically
//     const uniqueZoneNames = useMemo(() => {
//         return Array.from(new Set(groupLeaderChartData.map(item => item.zone_name)));
//     }, [groupLeaderChartData]);

//     const filteredData = useMemo(() => {
//         return groupLeaderChartData.filter(item => {
//             const itemYear = parseInt(item.audit_year, 10);
//             const itemMonth = parseInt(item.audit_month, 10);
//             return itemYear === selectedYear && selectedMonths.includes(itemMonth);
//         });
//     }, [groupLeaderChartData, selectedYear, selectedMonths]);

//     const radarData = useMemo(() => {
//         // Initialize radar data structure with zone names as subjects
//         const baseRadarData = uniqueZoneNames.map(zoneName => ({
//             subject: zoneName,
//             fullMark: 40
//         }));

//         // Populate radar data with scores for each selected month
//         selectedMonths.forEach((month, monthIndex) => {
//             baseRadarData.forEach(zoneData => {
//                 const zoneEntry = filteredData.find(item => item.zone_name === zoneData.subject && parseInt(item.audit_month, 10) === month);
//                 zoneData[`score_${month}`] = zoneEntry ? zoneEntry.total_score : 0;  // Use month-specific key for each score
//             });
//         });

//         return baseRadarData;
//     }, [filteredData, selectedMonths, uniqueZoneNames]);


//     const [lowScoreCount, highScoreCount, equalScoreCount] = useMemo(() => {
//         if (selectedMonths.length < 2) return [0, 0, 0];
        
//         // Sort selected months in ascending order and choose the latest two months
//         const sortedMonths = [...selectedMonths].sort((a, b) => a - b);
//         const [prevMonth, latestMonth] = sortedMonths.slice(-2);

//         let lowCount = 0;
//         let highCount = 0;
//         let equalCount = 0;

//         uniqueZoneNames.forEach(zoneName => {
//             const prevMonthScore = radarData.find(data => data.subject === zoneName)[`score_${prevMonth}`] || 0;
//             const latestMonthScore = radarData.find(data => data.subject === zoneName)[`score_${latestMonth}`] || 0;

//             if (prevMonthScore < latestMonthScore) {
//                 highCount++;
//             } else if (prevMonthScore > latestMonthScore) {
//                 lowCount++;
//             } else {
//                 equalCount++;
//             }
//         });

//         return [lowCount, highCount, equalCount];
//     }, [radarData, selectedMonths, uniqueZoneNames]);

//     const CustomLegend = () => (
//         <div style={{ display: 'flex', fontSize: '12px', color: '#17428B', marginBottom: '10px', justifyContent: 'space-evenly' }}>
//             <p><strong>High Score Zones:</strong> {highScoreCount}</p>
//             <p><strong>Low Score Zones:</strong> {lowScoreCount}</p>
//             <p><strong>Equal Score Zones:</strong> {equalScoreCount}</p>
//         </div>
//     );

//     const monthColors = selectedMonths.map((month, index) => `hsl(${(index * 60) % 360}, 100%, 50%)`);

//     return (
//         <Box sx={{ border: '1px solid #ccc', borderRadius: '5px', width: '100%' }}>
//             <Box sx={{ borderBottom: '1px solid #ccc', padding: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
//                 <Typography variant='h4' sx={{ color: '#17428B', fontSize: '16px', fontWeight: '600' }}>5S Audit Summary - India</Typography>
//                 <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px' }}>
//                     <div className="select-box">
//                         <select
//                             value={selectedYear}
//                             onChange={e => setSelectedYear(Number(e.target.value))}
//                             sx={{
//                                 height: "32px",
//                                 fontSize: "14px",
//                                 padding: "4px 0px",
//                                 minWidth: "75px",
//                                 width: "auto",
//                                 flexShrink: 0, // Prevents shrinking
//                               }}
//                         >
//                             {[2020, 2021, 2022, 2023, 2024].map(year => (
//                                 <option key={year} value={year}>{year}</option>
//                             ))}
//                         </select>
//                     </div>

//                     <FormControl sx={{ minWidth: 200 }}>
//                         <InputLabel>Month(s)</InputLabel>
//                         <Select
//                             multiple
//                             value={selectedMonths}
//                             onChange={handleMonthChange}
//                             renderValue={(selected) => selected.map(month => dayjs().month(month - 1).format('MMMM')).join(", ")}
//                             MenuProps={{ PaperProps: { style: { maxHeight: 250 } } }}
//                             sx={{
//                                 height: "32px",
//                                 fontSize: "14px",
//                                 padding: "4px 0px",
//                                 minWidth: "75px",
//                                 width: "auto",
//                                 flexShrink: 0, // Prevents shrinking
//                               }}
//                         >
//                             {monthOptions.map(option => (
//                                 <MenuItem key={option.value} value={option.value}>
//                                     <Checkbox checked={selectedMonths.indexOf(option.value) > -1} />
//                                     <ListItemText primary={option.label} />
//                                 </MenuItem>
//                             ))}
//                         </Select>
//                     </FormControl>
//                 </div>
//             </Box>

//             <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'center' }}>
//                 <RadarChart outerRadius={150} width={900} height={700} data={radarData}>
//                     <PolarGrid className="polar-grid" stroke="#d8d8d8" />
//                     <PolarAngleAxis
//                         dataKey="subject"
//                         tick={renderCustomTick}
//                         // tick={{ fontSize: 12, color: '#17428B' }}
//                         tickFormatter={(zoneName) => zoneName} // Ensure zone names appear
//                     />
//                     <PolarRadiusAxis
//                         angle={90}
//                         domain={[0, 40]}
//                         tickValues={[0, 15, 20, 25, 30, 35, 40]}
//                         stroke="#ccc"
//                         tickCount={7}
//                         // stroke="#ccc"
//                     />
//                     {selectedMonths.map((month, index) => (
//                         <Radar
//                             key={month}
//                             name={`Month ${dayjs().month(month - 1).format('MMMM')}`}
//                             dataKey={`score_${month}`}  // Use month-specific score key
//                             stroke={monthColors[index]}
//                             fill={monthColors[index]}
//                             fillOpacity={0.2}
//                         />
//                     ))}
//                     <Tooltip
//                         content={({ payload }) => {
//                             if (payload && payload.length) {
//                                 const { subject, ...scores } = payload[0].payload;
//                                 return (
//                                     <div style={{ backgroundColor: 'rgba(255, 255, 255, 0.8)', padding: '5px', borderRadius: '5px' }}>
//                                         <strong>{subject}</strong>
//                                         {Object.entries(scores).map(([key, value]) => (
//                                             <p key={key}>{key.replace('score_', 'Score for Month ')}: {value}</p>
//                                         ))}
//                                     </div>
//                                 );
//                             }
//                             return null;
//                         }}
//                     />
//                      <Legend content={<CustomLegend />} />
//                 </RadarChart>
//                 {/* <Legend verticalAlign="bottom" wrapperStyle={{ width: '100%', display: 'flex', justifyContent: 'center' }} /> */}
//             </Box>
//         </Box>
//     );
// };

// export default RadarChartComponent;



